"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.tryNameSchema = exports.nameEquivalencesSchema = exports.careerSitePreviewSchema = exports.CAREER_LOGO_NAME_MAX = exports.VENTURE_LOGO_MAX_BYTES = exports.CAREER_LOGO_MAX_BYTES = exports.CAREER_LOGO_TYPES = exports.updateCareerSiteSettingsSchema = exports.careerSiteSettingsSchema = exports.CAREER_ACCENT_LABELS = exports.CAREER_ACCENTS = exports.reopenJobSchema = exports.closeJobSchema = exports.JOB_CLOSE_OUTCOME_LABELS = exports.JOB_CLOSE_OUTCOMES = exports.resumeJobSchema = exports.holdJobSchema = exports.recordSpendSchema = exports.postingActionSchema = exports.updatePostingSchema = exports.publishPostingSchema = exports.formLinkPlaceSchema = exports.MAX_FORM_LINK_APPLICATIONS = exports.MAX_PLACES_AT_ONCE = exports.POSTING_STATUS_LABELS = exports.UNPUBLISH_REASONS = exports.DIRECT_SOURCE_LABEL = exports.DIRECT_SOURCE = exports.PUBLISHABLE_CHANNELS = exports.POSTING_CHANNEL_LABELS = exports.POSTING_CHANNELS = exports.POSTING_ENTITY = void 0;
exports.setSourceLabels = setSourceLabels;
exports.sourceLabelOverride = sourceLabelOverride;
exports.postingChannelName = postingChannelName;
exports.formLinkPlaceKey = formLinkPlaceKey;
exports.defaultCareerSiteSettings = defaultCareerSiteSettings;
const zod_1 = require("zod");
const calendars_1 = require("./calendars");
const money_1 = require("./money");
const schemas_1 = require("./schemas");
// S13 — job hub, postings and the public career site (JOB-10, JOB-11, POST-01 to
// POST-04, ADMIN-12; F5.15–F5.17, F6.01–F6.05, F6.07).
//
// Sources: master plan S13; E4; D8 (manual posting with tracked URLs); §L22 N-09 to
// N-13; §L25 job opening machine. Product-owner decisions of 2026-09-29
// (Docs/DECISIONS-LOG.md S13 Q1–Q12): the career site is plain server-made HTML; the
// channel list is fixed plus "Other"; referral links are per staff member per job;
// a confidential job is seen only by its recruiter, HR Lead and hiring manager; one
// career site for every venture.
exports.POSTING_ENTITY = 'posting';
// --- Channels (Q2) -----------------------------------------------------------------
/**
 * E4's channels, plus "Other" with a typed name until ADMIN-08 makes the list
 * editable in S31. `referral` postings are the per-person links of F6.05 (Q3); a
 * recruiter never publishes one from POST-01.
 */
exports.POSTING_CHANNELS = [
    'career_site',
    'bdjobs',
    'linkedin',
    'facebook',
    'agency',
    'referral',
    'other',
];
exports.POSTING_CHANNEL_LABELS = {
    career_site: 'Career site',
    bdjobs: 'BDJobs',
    linkedin: 'LinkedIn',
    facebook: 'Facebook',
    agency: 'Agency',
    referral: 'Employee referral',
    other: 'Other',
};
exports.PUBLISHABLE_CHANNELS = [
    'career_site',
    'bdjobs',
    'linkedin',
    'facebook',
    'agency',
    'other',
];
/**
 * E4: "never let an application exist without a source". An application with no
 * identifiable posting is stamped `direct` and flagged for review.
 */
exports.DIRECT_SOURCE = 'direct';
exports.DIRECT_SOURCE_LABEL = 'Direct / unknown';
/** Why a posting went offline. A hold's postings come back on resume (E4). */
// `places_filled` is F6.06's (S24 Q4): every place accepted or joined.
exports.UNPUBLISH_REASONS = ['manual', 'job_on_hold', 'job_closed', 'job_confidential', 'places_filled'];
exports.POSTING_STATUS_LABELS = {
    draft: 'Scheduled',
    published: 'Live',
    unpublished: 'Offline',
    expired: 'Expired',
};
/** The name a posting shows: "Other" postings show the board the recruiter typed. */
/**
 * S31 (ADMIN-08): the HR Lead's own names for sources, and the sources they added. Loaded once
 * per process (the API keeps it current on every change; the browser sets it at sign-in), so a
 * rename shows everywhere a source is named without each caller looking it up.
 */
let sourceLabels = new Map();
function setSourceLabels(labels) {
    sourceLabels = labels;
}
function sourceLabelOverride(key) {
    return sourceLabels.get(key);
}
function postingChannelName(channel, channelName) {
    if (channel === 'other' && channelName)
        return channelName;
    const builtIn = Object.entries(exports.POSTING_CHANNEL_LABELS).find(([key]) => key === channel)?.[1];
    return sourceLabels.get(channel) ?? builtIn ?? channel;
}
// --- Requests ------------------------------------------------------------------------
const channelNameSchema = zod_1.z.string().trim().min(2, 'Name the job board.').max(60);
const externalUrlSchema = zod_1.z.url('Enter the full web address, starting with https://').max(500);
function windowIssue(window, ctx) {
    if (window.goesLiveOn && window.expiresOn && window.expiresOn < window.goesLiveOn) {
        ctx.addIssue({ code: 'custom', path: ['expiresOn'], message: 'Close the link on or after the day it opens.' });
    }
}
/** Form links (DECISIONS-LOG 2026-10-27): the most places ticked in one go. */
exports.MAX_PLACES_AT_ONCE = 12;
/** The highest application limit a form link takes. */
exports.MAX_FORM_LINK_APPLICATIONS = 100_000;
const maxApplicationsSchema = zod_1.z
    .number()
    .int('Enter a whole number.')
    .min(1, 'Enter 1 or more.')
    .max(exports.MAX_FORM_LINK_APPLICATIONS, `Enter ${exports.MAX_FORM_LINK_APPLICATIONS.toLocaleString('en-US')} or fewer.`);
/** One place a form link is for: a built-in channel, an HR Lead's own source, or a typed "Other". */
exports.formLinkPlaceSchema = zod_1.z
    .object({
    channel: zod_1.z.enum(exports.PUBLISHABLE_CHANNELS),
    channelName: channelNameSchema.nullable().optional(),
    /** S31 (ADMIN-08): one of the HR Lead's own sources, picked under "Other" — stored as its own key. */
    sourceKey: zod_1.z.string().trim().regex(/^src_[a-z0-9_]{1,56}$/).optional(),
})
    .superRefine((place, ctx) => {
    if (place.channel === 'other' && !place.channelName && !place.sourceKey) {
        ctx.addIssue({ code: 'custom', path: ['channelName'], message: 'Name the place.' });
    }
});
/** Two ticks for the same place would make the same link twice. */
function formLinkPlaceKey(place) {
    if (place.sourceKey)
        return place.sourceKey;
    if (place.channel === 'other')
        return `other:${(place.channelName ?? '').trim().toLowerCase()}`;
    return place.channel;
}
/**
 * F6.01, and F6.02 when either date is given: one form link per place ticked, all with
 * the same window (DECISIONS-LOG 2026-10-27 "several at once"). Dates are Bangladesh
 * calendar days: a link opens at the start of `goesLiveOn` and closes at the end of
 * `expiresOn`.
 */
exports.publishPostingSchema = zod_1.z
    .object({
    places: zod_1.z.array(exports.formLinkPlaceSchema).min(1, 'Tick at least one place.').max(exports.MAX_PLACES_AT_ONCE),
    goesLiveOn: calendars_1.isoDateSchema.nullable().optional(),
    expiresOn: calendars_1.isoDateSchema.nullable().optional(),
    maxApplications: maxApplicationsSchema.nullable().optional(),
})
    .superRefine((input, ctx) => {
    const keys = input.places.map(formLinkPlaceKey);
    if (new Set(keys).size !== keys.length) {
        ctx.addIssue({ code: 'custom', path: ['places'], message: 'The same place is ticked twice.' });
    }
    windowIssue(input, ctx);
});
/** Changing the window or the advert's address on an existing posting. */
exports.updatePostingSchema = schemas_1.versionedSchema
    .extend({
    goesLiveOn: calendars_1.isoDateSchema.nullable().optional(),
    expiresOn: calendars_1.isoDateSchema.nullable().optional(),
    externalUrl: externalUrlSchema.nullable().optional(),
    maxApplications: maxApplicationsSchema.nullable().optional(),
})
    .superRefine(windowIssue);
/** F6.03, and putting an unpublished posting back (E4: same posting, same history). */
exports.postingActionSchema = schemas_1.versionedSchema;
/** F6.04. Money is minor units and an ISO code (non-negotiable #7). */
exports.recordSpendSchema = zod_1.z.object({
    amountMinor: money_1.minorUnitsSchema.refine((amount) => amount > 0n, 'Enter a valid amount.'),
    currency: money_1.currencyCodeSchema,
    description: zod_1.z.string().trim().min(2, 'Say what the money was spent on.').max(200),
    incurredOn: calendars_1.isoDateSchema.optional(),
});
// --- Job lifecycle (F5.15–F5.17, §L25) ----------------------------------------------
const lifecycleReasonSchema = zod_1.z.string().trim().max(1000);
/** F5.15: §L25 open → on_hold. Reason mandatory; postings come down; clocks pause. */
exports.holdJobSchema = schemas_1.versionedSchema.extend({
    reason: lifecycleReasonSchema.min(3, 'Pick a reason before continuing.'),
});
exports.resumeJobSchema = schemas_1.versionedSchema;
/**
 * F5.16, HR Lead only. `closed_partial` closes with slots still empty; `cancelled`
 * needs a reason and triggers §L13. A full close (`filled`) is the system's, when the
 * last hire joins (S24).
 */
exports.JOB_CLOSE_OUTCOMES = ['closed_partial', 'cancelled'];
exports.JOB_CLOSE_OUTCOME_LABELS = {
    closed_partial: 'Close with seats unfilled',
    cancelled: 'Cancel the job',
};
exports.closeJobSchema = schemas_1.versionedSchema
    .extend({
    outcome: zod_1.z.enum(exports.JOB_CLOSE_OUTCOMES),
    reason: lifecycleReasonSchema.optional(),
})
    .superRefine((input, ctx) => {
    if (input.outcome === 'cancelled' && (input.reason ?? '').length < 3) {
        ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Pick a reason before continuing.' });
    }
});
/** F5.17: filled / closed_partial → reopened (HR Lead), then reopened → open. */
exports.reopenJobSchema = schemas_1.versionedSchema.extend({ reason: lifecycleReasonSchema.optional() });
// --- Career site settings (ADMIN-12) -------------------------------------------------
/**
 * The accent is a Flock token, never a colour someone types: the career site stays
 * inside the design system (CLAUDE.md §5) while still letting HR choose a look.
 */
exports.CAREER_ACCENTS = ['blue', 'deep', 'accent'];
exports.CAREER_ACCENT_LABELS = {
    blue: 'Nest blue',
    deep: 'Deep navy',
    accent: 'Warm orange',
};
const MAX_CAREER_SECTIONS = 4;
exports.careerSiteSettingsSchema = zod_1.z.object({
    siteTitle: zod_1.z.string().trim().min(2, 'Give the site a name.').max(80),
    headline: zod_1.z.string().trim().min(2, 'Write a headline.').max(120),
    intro: zod_1.z.string().trim().max(2000).nullable(),
    sections: zod_1.z
        .array(zod_1.z.object({
        title: zod_1.z.string().trim().min(1, 'Give the section a title.').max(80),
        body: zod_1.z.string().trim().min(1, 'Write something in the section.').max(3000),
    }))
        .max(MAX_CAREER_SECTIONS, `At most ${MAX_CAREER_SECTIONS} sections.`),
    accent: zod_1.z.enum(exports.CAREER_ACCENTS),
    contactEmail: schemas_1.emailSchema.nullable(),
});
exports.updateCareerSiteSettingsSchema = exports.careerSiteSettingsSchema.extend(schemas_1.versionedSchema.shape);
function defaultCareerSiteSettings() {
    return {
        siteTitle: 'Careers',
        headline: 'Find your next role with us',
        intro: null,
        sections: [],
        accent: 'blue',
        contactEmail: null,
    };
}
/** §L26 photo rule, tightened: the logo rides every career page on a 3G phone. */
exports.CAREER_LOGO_TYPES = ['image/png', 'image/jpeg'];
exports.CAREER_LOGO_MAX_BYTES = 200 * 1024;
/** A venture's own logo, set on ADMIN-15 — the career logo's rule (DECISIONS-LOG 2026-10-06). */
exports.VENTURE_LOGO_MAX_BYTES = exports.CAREER_LOGO_MAX_BYTES;
/** The longest logo file name kept; a longer one is cut, keeping its extension. */
exports.CAREER_LOGO_NAME_MAX = 120;
/**
 * ADMIN-12's live preview (Admin redesign 2026-11-02, Option A of `4-messages/career-site.html`): the settings as
 * typed, not yet saved. Looser than the saved shape on purpose — a headline half-written still draws — but bounded the
 * same way, so a preview can never be larger than a saved site.
 */
exports.careerSitePreviewSchema = zod_1.z.object({
    siteTitle: zod_1.z.string().trim().max(80),
    headline: zod_1.z.string().trim().max(120),
    intro: zod_1.z.string().trim().max(2000).nullable(),
    sections: zod_1.z
        .array(zod_1.z.object({ title: zod_1.z.string().trim().max(80), body: zod_1.z.string().trim().max(3000) }))
        .max(MAX_CAREER_SECTIONS, `At most ${MAX_CAREER_SECTIONS} sections.`),
    accent: zod_1.z.enum(exports.CAREER_ACCENTS),
    contactEmail: zod_1.z.string().trim().max(254).nullable(),
});
// --- §L2 transliteration table, maintained at ADMIN (S13 Q9) ---------------------------
const MAX_NAME_GROUPS = 200;
const MAX_SPELLINGS = 12;
/** One name, several spellings. The first is the one every other is read as. */
exports.nameEquivalencesSchema = zod_1.z
    .object({
    groups: zod_1.z
        .array(zod_1.z
        .array(zod_1.z
        .string()
        .trim()
        .toLowerCase()
        .min(2, 'A spelling needs at least two letters.')
        .max(40)
        .regex(/^\p{L}+$/u, 'Letters only — one word per spelling.'))
        .min(2, 'A name needs at least two spellings.')
        .max(MAX_SPELLINGS, `At most ${MAX_SPELLINGS} spellings of one name.`))
        .max(MAX_NAME_GROUPS),
    /**
     * The table's `version` when the edit began (Admin redesign 2026-11-02). A save from an older table is refused, so
     * two Admins never overwrite each other. Left out by older callers, which then save as before.
     */
    basedOn: zod_1.z.string().min(1).max(64).optional(),
})
    .superRefine((input, ctx) => {
    const seen = new Map();
    input.groups.forEach((group, index) => {
        for (const spelling of group) {
            const first = seen.get(spelling);
            if (first !== undefined) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['groups', index],
                    message: `"${spelling}" is already in name ${first + 1}. A spelling can belong to one name only.`,
                });
            }
            seen.set(spelling, index);
        }
    });
});
/** "Try a name" (Admin redesign 2026-11-02): one name, read under the saved table. */
exports.tryNameSchema = zod_1.z.object({ name: zod_1.z.string().trim().min(1, 'Type a name.').max(200) });
//# sourceMappingURL=publishing.js.map